import { inject, Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse, HttpContextToken } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { Router } from "@angular/router";
import { ToastService } from "@app/kernel/tools/message/message.config";
import { isObject } from "@app/kernel/shared/helpers";
import { AuthService } from "@app/shared/auth/services/auth.service";
import { RESTRICTED_ACCESS_TYPES } from "@app/shared/auth/enums/access-type.enum";

interface IErrorClassification {
  isUnauthorized: boolean;
  isForbidden: boolean;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private router = inject(Router);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const disableToastFn = this.getDisableToastFn(req);
    const disableAuthHandleFn = this.getDisableAuthHandleFn(req);

    return next.handle(req).pipe(
      tap({
        next: event => {
          if (event instanceof HttpResponse && Array.isArray(event.body)) {
            const { isUnauthorized, isForbidden } = this.handleGraphQLErrors(event.body, disableToastFn, disableAuthHandleFn);

            if (isUnauthorized) {
              this.handleUnauthorized();
            } else if (isForbidden) {
              this.handleForbidden();
            }
          }
        },
        error: (response: HttpErrorResponse) => {
          if (!disableAuthHandleFn(response.error)) {
            if (response.status === 401) {
              this.handleUnauthorized();
            } else if (response.status === 403) {
              this.handleForbidden();
            }
          }

          if (!disableToastFn(response.error)) {
            const messageKey = response.error?.data?.messageKey ?? response.error?.message ?? "ERRORS.SOMETHING_WENT_WRONG";
            this.toast.error(messageKey, "ERRORS.SUMMARY");
          }
        },
      })
    );
  }

  private handleUnauthorized(): void {
    if (this.hasRestrictedToken()) {
      this.router.navigate(['/auth/change-temporary-password']);
    } else {
      this.authService.logout().subscribe();
    }
  }

  private handleForbidden(): void {
    this.toast.error("ERRORS.ACCESS_DENIED", "ERRORS.SUMMARY");
  }

  private handleGraphQLErrors(
    responseItems: unknown[],
    disableToastFn: (error: unknown) => boolean | null,
    disableAuthHandleFn: (error: unknown) => boolean | null
  ): IErrorClassification {
    let isUnauthorized = false;
    let isForbidden = false;

    for (const item of responseItems) {
      const gqlErrors = isObject(item) && "errors" in item && item?.errors;
      if (!Array.isArray(gqlErrors)) continue;

      for (const error of gqlErrors) {
        const formatted = error?.extensions?.formatted ?? error?.extensions;
        const { code, type, message, data = error?.extensions } = formatted || {};

        if (!disableAuthHandleFn(formatted)) {
          if (code === 401 || type === "UNAUTHORIZED") {
            isUnauthorized = true;
          } else if (code === 403 || type === "FORBIDDEN") {
            isForbidden = true;
          }
        }

        if (!disableToastFn(formatted) && type !== "FORBIDDEN") {
          const messageKey = data?.messageKey ?? message ?? "ERRORS.SOMETHING_WENT_WRONG";
          this.toast.error(messageKey, "ERRORS.SUMMARY");
        }
      }
    }

    return { isUnauthorized, isForbidden };
  }

  private hasRestrictedToken(): boolean {
    try {
      const token = this.authService.getToken();
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return RESTRICTED_ACCESS_TYPES.includes(payload.accessType);
    } catch {
      return false;
    }
  }

  private getDisableToastFn(req: HttpRequest<unknown>) {
    return (err: any) => false;
  }

  private getDisableAuthHandleFn(req: HttpRequest<unknown>) {
    return (err: any) => false;
  }
}
